/**
 * Reusable Checkbox Group Component
 * For rendering multiple checkboxes with shared state
 */

import React from 'react';
import Checkbox from './Checkbox';
import { clsx } from 'clsx';

export interface CheckboxGroupOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface CheckboxGroupProps {
  name: string;
  value?: string[];
  onChange?: (values: string[]) => void;
  options: CheckboxGroupOption[] | string[];
  label?: string;
  error?: string;
  helperText?: string;
  orientation?: 'horizontal' | 'vertical';
  fullWidth?: boolean;
  className?: string;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  value = [],
  onChange,
  options,
  label,
  error,
  helperText,
  orientation = 'vertical',
  fullWidth = true,
  className,
}) => {
  // Normalize options to array of {value, label} objects
  const normalizedOptions: CheckboxGroupOption[] = options.map((option) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    return option;
  });

  const handleChange = (optionValue: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? [...value] : [];
    
    if (checked) {
      // Add value if not already present
      if (!currentValues.includes(optionValue)) {
        onChange?.([...currentValues, optionValue]);
      }
    } else {
      // Remove value
      onChange?.(currentValues.filter((v) => v !== optionValue));
    }
  };

  return (
    <div className={clsx({ 'w-full': fullWidth }, className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <div
        className={clsx(
          'space-y-2',
          {
            'flex flex-col space-y-2': orientation === 'vertical',
            'flex flex-row space-x-4 flex-wrap': orientation === 'horizontal',
          }
        )}
        role="group"
        aria-labelledby={label ? `${name}-label` : undefined}
      >
        {normalizedOptions.map((option, index) => {
          const optionId = `${name}-${option.value}-${index}`;
          const isChecked = Array.isArray(value) && value.includes(option.value);
          
          return (
            <Checkbox
              key={optionId}
              id={optionId}
              name={name}
              value={option.value}
              checked={isChecked}
              onChange={(e) => handleChange(option.value, e.target.checked)}
              disabled={option.disabled}
              label={option.label}
            />
          );
        })}
      </div>
      
      {error && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p
          id={`${name}-helper`}
          className="mt-1 text-sm text-gray-500"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default CheckboxGroup;

